import React, { useEffect, useRef, useState } from 'react';
import { Image, Platform, ScrollView, View } from 'react-native';

import styles from './definitions-image.style';
import { useActivityContext } from '../../../hooks/use-activity.context';
import { useScroll } from '../../../hooks/use-scroll';
import { arrayShuffle } from '../../../utils/array';
import trueBeginnerStr from '../../../utils/true-beginner-str';
import { CollapsableText } from '../../collapsable-text/collapsable-text';
import { ContinueButton } from '../../continue-button/continue-button';
import { Instruction } from '../../instruction/instruction.component';
import { OptionChooseContainer } from '../../option-choose-container/option-choose-container';
import { ThemedView } from '../../themed-view';

export function DefinitionsImage() {
  const {
    currentStep,
    currentActivity,
    isCurrentStepComplete,
    goNext,
    completeCurrentStep,
  } = useActivityContext();

  const [correctFeedback, setCorrectFeedback] = useState(true);
  const [showNextButton, setShowNextButton] = useState(false);
  const scrollRef = useRef<ScrollView>(null);
  const { scrollToEnd, scrollToTop } = useScroll(scrollRef);

  const [options, setOptions] = useState([]);

  useEffect(() => {
    if (
      !currentActivity.trueBeginner ||
      !currentStep?.answerTranslation ||
      !currentStep?.optionsTranslation
    ) {
      setOptions(arrayShuffle([currentStep?.answer, ...currentStep?.options]));
    } else {
      setOptions(
        arrayShuffle([
          currentStep?.answerTranslation,
          ...currentStep?.optionsTranslation,
        ])
      );
    }
    disableOptions(false);
  }, [
    currentActivity.trueBeginner,
    currentStep?.answer,
    currentStep?.answerTranslation,
    currentStep?.options,
    currentStep?.optionsTranslation,
  ]);

  const [optionsAreDisabled, disableOptions] = useState(false);
  const [index, setIndex] = useState(0);

  const onAnswer = (isCorrect: boolean) => {
    disableOptions(true);
    setTimeout(() => {
      setCorrectFeedback(isCorrect);
      completeCurrentStep(isCorrect);
      setShowNextButton(true);
    }, 800);
  };

  useEffect(() => {
    if (isCurrentStepComplete) {
      scrollToEnd();
    } else {
      scrollToTop();
    }
  }, [isCurrentStepComplete, scrollToEnd, scrollToTop]);

  const resetAndGoNextStep = () => {
    setShowNextButton(false);
    goNext();
    disableOptions(false);
    setIndex((x) => x + 1);
  };

  return (
    <>
      {/* @ts-ignore */}
      <ScrollView ref={scrollRef} contentContainerStyle={styles.container}>
        <View style={styles.content}>
          <Instruction
            text={trueBeginnerStr(
              currentActivity,
              'instructions',
              currentActivity.trueBeginner
            )}
          />
          {!!currentStep?.image && (
            <Image style={styles.image} source={{ uri: currentStep?.image }} />
          )}
          <ThemedView style={[styles.view]} />
          {!!currentStep?.audio && (
            <View style={styles.floatView}>
              <CollapsableText
                translationText={currentStep?.textTranslation}
                text={currentStep?.text}
                showHideBottom={false}
              />
            </View>
          )}
        </View>
        <OptionChooseContainer
          containerStyle={styles.optionContainer}
          disabled={optionsAreDisabled}
          answer={trueBeginnerStr(
            currentStep,
            'answer',
            currentActivity.trueBeginner
          )}
          options={options}
          onAnswer={onAnswer}
          index={index}
          scrollToEnd={scrollToEnd}
          checkButtonActive={Platform.OS !== 'web'}
        />
      </ScrollView>
      {showNextButton && (
        <ContinueButton
          feedbackTitle={currentStep?.feedbackTitle}
          feedback={currentStep?.feedback}
          isCorrect={correctFeedback}
          onPress={resetAndGoNextStep}
        />
      )}
    </>
  );
}
